/**
 * tile-math.ts
 *
 * Web Mercator tile math shared by the place clustering and the prospecting grid.
 * These invert / match BRDB.Repository.PlaceRepository.LatLngToTile and the
 * backend GeoGrid.TileToBbox so a tile (zoom, tileX, tileY) means the same box on
 * both sides.
 */
import L from 'leaflet';

/** The geographic bounds of a single Web Mercator tile. */
export function tileBoundsAtZoom(tileX: number, tileY: number, zoom: number): L.LatLngBounds {
  const n = 2 ** zoom;
  const west = tileX / n * 360 - 180;
  const east = (tileX + 1) / n * 360 - 180;
  const north = Math.atan(Math.sinh(Math.PI * (1 - 2 * tileY / n))) * 180 / Math.PI;
  const south = Math.atan(Math.sinh(Math.PI * (1 - 2 * (tileY + 1) / n))) * 180 / Math.PI;
  return L.latLngBounds([south, west], [north, east]);
}

/** Geometric centre of a tile. */
export function tileCenterAtZoom(tileX: number, tileY: number, zoom: number): L.LatLng {
  return tileBoundsAtZoom(tileX, tileY, zoom).getCenter();
}

/** The tile (tileX, tileY) at the given zoom that contains a lat/lng. */
export function latLngToTile(lat: number, lng: number, zoom: number): { tileX: number; tileY: number } {
  const n = 2 ** zoom;
  const tileX = Math.floor((lng + 180) / 360 * n);
  const latRad = lat * Math.PI / 180;
  const tileY = Math.floor((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n);
  return { tileX, tileY };
}

/** Identifies which tile contains an L.LatLng. */
export function tileContainingLatLng(latLng: L.LatLng, zoom: number): { tileX: number; tileY: number } {
  return latLngToTile(latLng.lat, latLng.lng, zoom);
}
